import {
  useReactTable as useTanstackReactTable,
  type CellContext,
  type RowSelectionState,
  type TableOptions,
  type Updater,
} from '@tanstack/react-table';
import * as React from 'react';

import { DataTableCell } from './data-table-cell';
import type { DataTableColumnMeta } from './data-table-types';
import { omitUndefined, type Omittable } from '../../lib/props';

/**
 * A wrapper around TanStack's useReactTable that provides a default cell renderer.
 *
 * The default cell renderer automatically wraps primitive values in DataTableCell,
 * applying proper alignment (right for numeric columns) and truncation based on
 * column meta configuration.
 *
 * Renamed from `useReactTable` to avoid collision with TanStack's own export.
 *
 * @example
 * ```tsx
 * const table = useDataTable({
 *   data,
 *   columns,
 *   getCoreRowModel: getCoreRowModel(),
 * });
 * ```
 */
export function useDataTable<TData>(options: Omittable<TableOptions<TData>>) {
  const defaultColumn = React.useMemo(() => {
    const userDefaultColumn = options.defaultColumn ?? {};

    if (userDefaultColumn.cell) {
      return userDefaultColumn;
    }

    return {
      ...userDefaultColumn,
      cell: (context: CellContext<TData, unknown>) => {
        const meta = context.column.columnDef.meta as DataTableColumnMeta | undefined;
        const rendered = context.renderValue();

        if (rendered === null || rendered === undefined) {
          return null;
        }

        if (React.isValidElement(rendered)) {
          return rendered;
        }

        const align = meta?.isNumeric ? 'end' : 'start';

        return (
          <DataTableCell
            truncate={meta?.truncate ?? true}
            align={align}
            textBrightness={meta?.textBrightness}
          >
            {rendered as React.ReactNode}
          </DataTableCell>
        );
      },
    };
  }, [options.defaultColumn]);

  const onRowSelectionChange = options.onRowSelectionChange
    ? (updater: Updater<RowSelectionState>) => {
        options.onRowSelectionChange?.((previous) => {
          const next =
            typeof updater === 'function' ? updater(previous) : updater;
          return { ...(next ?? {}) };
        });
      }
    : undefined;

  return useTanstackReactTable(
    omitUndefined({
      ...options,
      defaultColumn,
      onRowSelectionChange,
    }),
  );
}
